/**
 * pages/signup/steps/Name.tsx — legal name entry.
 *
 * Client-side validation is minimum-viable: length > 1, no leading/
 * trailing whitespace. Everything else is the vendor's job.
 */

import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button, Field } from "../../../components";
import { advance } from "@aster/onboarding";
import { getProgress, setProgress } from "../signup-store";
import { SignupShell } from "../SignupShell";

export function SignupName() {
  const navigate = useNavigate();
  const cur = getProgress();

  // Enforce the linear flow: if the user landed here without accepting
  // legal first (deep link, unusual state), bounce them back.
  if (!cur || !cur.legal_accepted_at) {
    return <Navigate to="/signup/legal" replace />;
  }

  const [name, setName] = useState<string>(cur.legal_name ?? "");
  const [err, setErr] = useState<string | null>(null);

  const trimmed = name.trim();
  const isValid = trimmed.length >= 2 && trimmed.length <= 100;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isValid) {
      setErr("Please enter your full legal name (2–100 characters).");
      return;
    }
    setProgress(advance(cur, "dob", { legal_name: trimmed }));
    navigate("/signup/dob");
  };

  return (
    <SignupShell
      title="What's your legal name?"
      subtitle="Use the name that matches your government-issued ID. It's what your account and card will read."
      step={2}
      backTo="/signup/legal"
      errorBanner={err}
    >
      <form onSubmit={onSubmit}>
        <Field
          label="Full legal name"
          value={name}
          onChange={setName}
          placeholder="Ada Lovelace"
          autoComplete="name"
          maxLength={100}
        />
        <Button type="submit" disabled={!isValid}>
          Continue
        </Button>
      </form>
    </SignupShell>
  );
}
